import { useState, useMemo, useCallback, useEffect, useRef } from "react";
import { Box, Divider } from "@mui/material";
import FieldsTable from "@/Components/Tables/FieldsTable";
import { SectionHeader } from "@/Components/Header/SectionsHeader";
import { LuX } from "@/Components/Icons/Icons";
import { OutLinedButton } from "@/Components/Buttons/OutLinedButton";
import { BlackButton } from "@/Components/Buttons/FiledButton";
import InputTextField from "../../../Components/Form/InputTextField";
import { useUser } from "../../../Context/UserContext";
import FormDialog from "@/Components/Dialog/FormDialog";
import trackingschema from "../../../Config/Export/CreateNewJob/Tracking/Tracking";
import FormGenerator from "../../../Utils/FormGenerator/FormGenerator";
import trackingheaderschema from "../../../Config/Export/CreateNewJob/Tracking/Trackingheader";
import trackingsbrecipt from "../../../Config/Export/CreateNewJob/Tracking/Trackingsbrecipt";
import TrackingGoodsRecipt from "../../../Views/ExportJob/CreateNewJob/GoodsRecipt";

export default function Tracking({ onClose, dynamicTrackingRef }) {
  const formRef = useRef();
  // const dynamicTrackingRef = useRef({});
  const [rows, setRows] = useState([]);

  const [showGoodsDialog, setShowGoodsDialog] = useState(false); // ✅ ONLY goods receipt
  const [currentSchema, setCurrentSchema] = useState(null);
  const setIsRequiredFieldFilled = useUser((s) => s.setIsRequiredFieldFilled);
  const loading = useUser((s) => s.loading);
  const [showForm, setShowForm] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [prefillData, setPrefillData] = useState({});
  console.log(
    "🚀 ~ file: Tracking.jsx:108 ~ Tracking ~ prefillData:",
    dynamicTrackingRef
  );
  const formData = useUser((s) => s.formData);
  const headerPrefill = formData?.tracking?.[0] || {};
  const setFormData = useUser((s) => s.setFormData); // --------------------------------------------
  const [totalProductValue, setTotalProductValue] = useState();
  // FIXED MASTER LIST (MEMOIZED)
  const TRACKING_KEY_MAP = {
    "SB Filed": "sbFiled",
    "SB Receipt": "sbReceipt",
    Assessed: "assessed",
    "Goods Receipt": "goodsReceipt", // if backend has this
    Carted: "carted",
    Examined: "examined",
    "L.E.O": "leo",
    Stuffing: "stuffing",
    "Hand over to carrier": "handOverCarrier",
    Loaded: "loaded",
    "BL Release": "blRelease",
    "EP Copy": "epCopy",
    Billing: "billing",
    "Reach at Destination": "reachAtDestination",
    "Delivery to Consignee": "deliveryToConsignee",
  };
  // --------------------------------------------
  const CHARGE_MASTER = useMemo(
    () => [
      "SB Filed",
      "SB Receipt",
      "Assessed",
      "Goods Receipt",
      "Carted",
      "Examined",
      "L.E.O",
      "Stuffing",
      "Hand over to carrier",
      "Loaded",
      "BL Release",
      "EP Copy",
      "Billing",
      "Reach at Destination",
      "Delivery to Consignee",
    ],
    []
  );
  const handleCloseForm = async () => {
    if (selectedRow?.Milestone?.value?.[0] !== "Goods Receipt") {
      const formValues = await formRef.current?.submit?.();

      console.log("✅ formValues on close:", formValues);

      if (formValues && selectedRow?._apiKey) {
        // ✅ ALWAYS ensure object exists before spreading
        dynamicTrackingRef.current[selectedRow._apiKey] = {
          ...(dynamicTrackingRef.current[selectedRow._apiKey] || {}), // ✅ NULL SAFE
          ...formValues,
        };

        console.log(
          "✅ SAFE AUTO SAVED TO dynamicTrackingRef:",
          dynamicTrackingRef.current
        );

        setFormData((prev) => ({
          ...prev,
          tracking: [
            {
              ...(prev.tracking?.[0] || {}),
              ...dynamicTrackingRef.current,
            },
          ],
        }));
      }
    }

    setShowForm(false);
    setShowGoodsDialog(false);
    setIsEditing(false);
    setSelectedRow(null);
    setCurrentSchema(null);
  };

  const SCHEMA_BY_MILESTONE = {
    "SB Receipt": trackingsbrecipt,
    // "Goods Receipt": GoodsReceiptSchema,
  };

  // ✅ fallback → default schema
  const getSchemaByMilestone = (milestoneName) => {
    return SCHEMA_BY_MILESTONE[milestoneName] || trackingschema;
  };

  const staticRows = useMemo(() => {
    return CHARGE_MASTER.map((label, i) => ({
      id: i + 1,
      editable: true,
      Milestone: { type: ["display"], value: [label] },
      // ⛔ Default disabled
      plandate: {
        type: ["DatetimeSelector"],
        value: [""],
        width: ["250px"],
        maxDateToToday: [false],
      },
      actualdate: {
        type: ["DatetimeSelector"],
        value: [""],
        width: ["250px"],
        maxDateToToday: [true],
      },
      actions: {
        type: ["action"],
        value: [""],
        width: ["100px"],
        onlyEdit: true, // ✅ ONLY EDIT ICON HERE
      },
    }));
  }, [CHARGE_MASTER]);

  useEffect(() => {
    if (rows.length === 0) {
      setRows(staticRows);
    }
  }, [rows.length, staticRows]);

  // --------------------------------------------
  // --------------------------------------------
  const handleEditRow = (row) => {
    setSelectedRow(row);

    const apiKey = row._apiKey;
    const milestoneName = row.Milestone?.value?.[0];

    // ✅ ✅ ✅ CORRECT PREFILL SOURCE (BOTH STRUCTURES HANDLED)
    const trackingDataForRow =
      formData?.tracking?.[apiKey] || formData?.tracking?.[0]?.[apiKey] || {};

    setPrefillData(trackingDataForRow);

    const schemaToOpen = getSchemaByMilestone(milestoneName);

    setCurrentSchema(schemaToOpen);

    setIsEditing(true);
    setShowForm(true);

    if (milestoneName === "Goods Receipt") {
      setShowGoodsDialog(true);
      setShowForm(false);
    }
  };

  useEffect(() => {
    const tracking = formData?.tracking?.[0] || {}; // ✅ FIXED

    const newRows = CHARGE_MASTER.map((label, i) => {
      const apiKey = TRACKING_KEY_MAP[label];
      const rowData = tracking?.[apiKey] || {};

      return {
        id: i + 1,
        editable: true,
        _apiKey: apiKey,

        Milestone: {
          type: ["display"],
          value: [label],
        },

        plandate: {
          type: ["DatetimeSelector"],
          value: [rowData?.planDate || ""],
          width: ["250px"],
          maxDateToToday: [false],
        },

        actualdate: {
          type: ["DatetimeSelector"],
          value: [rowData?.actualDate || ""],
          width: ["250px"],
          maxDateToToday: [true],
        },

        actions: {
          type: ["action"],
          value: [""],
          width: ["100px"],
          onlyEdit: true,
        },
      };
    });

    setRows(newRows);
  }, [formData, CHARGE_MASTER]);

  // --------------------------------------------

  const columnFields = useMemo(
    () => [
      { key: "Milestone", label: "Charges", width: "200px" },
      { key: "plandate", label: "Plan Date", width: "200px" },
      { key: "actualdate", label: "Actual Date", width: "200px" },
      { key: "actions", label: "Actions", width: "80px" },
    ],
    []
  );

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        backgroundColor: "var(--gray-0)",
        width: "100%",
        height: 382,
      }}
    >
      <Box
        sx={{
          p: 2,
        }}
      >
        <FormGenerator
          schema={trackingheaderschema}
          initialData={headerPrefill} // ✅ THIS IS THE KEY FIX
          ref={formRef} // ✅ ONLY formRef here
        />{" "}
      </Box>
      {/* Table */}
      <FieldsTable
        rowData={rows}
        columnFields={columnFields}
        onEdit={handleEditRow}
      />
      {showGoodsDialog && (
        <FormDialog
          title="Goods Receipt"
          onClose={handleCloseForm}
          showCancelButton={true}
          showSubmitButton={true}
          cancelLabel="Close"
          submitLabel="Save"
        >
          <TrackingGoodsRecipt
            rowData={selectedRow}
            onClose={handleCloseForm}
          />
        </FormDialog>
      )}
      {showForm && currentSchema && (
        <FormDialog
          title={`${selectedRow?.Milestone?.value?.[0]}`}
          schema={currentSchema}
          isEditing={isEditing}
          onSubmit={() => handleSave("table")}
          onClose={handleCloseForm}
          initialData={prefillData} // ✅ THIS IS THE KEY LINE
          showCancelButton={true}
          showSubmitButton={false}
          cancelLabel="Cancel"
          // submitLabel={isEditing ? "Save" : "Add"}
          loading={loading}
          ref={formRef} // ✅ ONLY formRef here
        />
      )}
    </Box>
  );
}
